import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import type { CompanyWorker } from "model/Company";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { WidthWrapper } from "./workerDropDown.styled";

type Props = {
  readonly disabled?: boolean;
  readonly lazyLoad?: boolean;
  readonly onlyActiveWorkers: boolean;
  readonly selectedWorker: CompanyWorker | undefined;
  readonly shouldSelectOnEnterOrTab?: boolean;
  readonly width?: number;
  readonly setSelectedWorker: React.Dispatch<
    React.SetStateAction<CompanyWorker | undefined>
  >;
};

export const WorkerDropdown: React.FC<Props> = (props) => {
  const [workers, setWorkers] = useState<CompanyWorker[]>([]);
  const [hasRequestedWorkers, setHasRequestedWorkers] = useState(false);
  const [{ company }] = useCompanyStore();
  const { t } = useTranslation();
  const shouldLoadWorkers = !props.lazyLoad || hasRequestedWorkers;

  useEffect(() => {
    if (!company?.id || !shouldLoadWorkers) return;
    let isCancelled = false;
    setWorkers([]);
    Api()
      .company.worker.getCompanyWorkersV2List(
        company.id,
        props.onlyActiveWorkers,
      )
      .then((res) => {
        if (!isCancelled) setWorkers(res.data);
      })
      .catch(() => {
        if (!isCancelled) setWorkers([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [company?.id, props.onlyActiveWorkers, shouldLoadWorkers]);

  const options = useMemo<SearchableDropdownOption[]>(
    () =>
      workers.map((w) => ({
        value: w.id.toString(),
        label: `${w.firstName} ${w.lastName}`,
        rightText: `[ID: ${w.id}]`,
      })),
    [workers],
  );

  const handleChange = (value: string): void => {
    const worker = workers.find((w) => w.id.toString() === value);
    props.setSelectedWorker(worker);
  };

  const dropdown = (
    <SearchableDropdown
      options={options}
      value={props.selectedWorker?.id.toString()}
      placeholder={t("Salary.SearchForWorker")}
      disabled={props.disabled}
      shouldSelectOnEnterOrTab={props.shouldSelectOnEnterOrTab}
      fullWidth={props.width !== undefined}
      onChange={handleChange}
      onClear={() => props.setSelectedWorker(undefined)}
      onFocus={() => {
        if (!props.lazyLoad) return;
        setHasRequestedWorkers(true);
      }}
    />
  );

  if (props.width !== undefined) {
    return <WidthWrapper $width={props.width}>{dropdown}</WidthWrapper>;
  }

  return dropdown;
};
